import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { navigation } from '@/routes/nav';
import { hasPermission } from '@/routes/guards';

const LOGO_MARK = 'https://framerusercontent.com/images/eD8yvvIWisGG6ewmQAjgrazbLY.jpeg';

interface AppSidebarProps {
  collapsed?: boolean;
}

export function AppSidebar({ collapsed }: AppSidebarProps) {
  return (
    <aside
      data-collapsed={collapsed || undefined}
      aria-label="Primary navigation"
      className={cn(
        'flex h-full flex-col border-r bg-[hsl(var(--sidebar-bg))] text-[hsl(var(--sidebar-fg))] transition-[width] duration-200',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      <div className="flex h-14 items-center gap-2 border-b px-3">
        <img
          src={LOGO_MARK}
          alt="AlignMate"
          className="h-9 w-9 shrink-0 rounded-lg object-cover shadow-card-sm"
          draggable={false}
        />
        {!collapsed && (
          <div className="flex min-w-0 flex-col leading-tight">
            <span className="font-display text-sm font-extrabold">AlignMate</span>
            <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Admin</span>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto scrollbar-thin px-2 py-3">
        {navigation.map((section) => {
          const items = section.items.filter((i) => !i.permission || hasPermission(i.permission));
          if (items.length === 0) return null;
          return (
            <div key={section.label} className="mb-4">
              {!collapsed && (
                <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {section.label}
                </div>
              )}
              <ul className="space-y-0.5">
                {items.map((item) => {
                  const Icon = item.icon;
                  const prefix = item.matchPrefix ?? item.href;
                  return (
                    <li key={item.href}>
                      <NavLink
                        to={item.href}
                        end={item.href === '/dashboard'}
                        className={({ isActive }) => {
                          const locationPrefixActive =
                            typeof window !== 'undefined' &&
                            window.location.pathname.startsWith(prefix);
                          const active = isActive || locationPrefixActive;
                          return cn(
                            'group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors',
                            active
                              ? 'bg-[hsl(var(--sidebar-active))] text-[hsl(var(--sidebar-active-fg))]'
                              : 'hover:bg-accent hover:text-accent-foreground',
                            collapsed && 'justify-center px-0',
                          );
                        }}
                      >
                        <Icon className="h-4 w-4 shrink-0" aria-hidden />
                        {!collapsed && <span className="truncate">{item.label}</span>}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      {!collapsed && (
        <div className="border-t px-3 py-2 text-[11px] text-muted-foreground">
          <div>AlignMate Admin · v1.0</div>
          <div className="truncate">fresh build · ADR-023</div>
        </div>
      )}
    </aside>
  );
}
